import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { useContext, useEffect, useState } from "react";

import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";

import {
  createOrganizationSubscription,
  getOrganizationSubscriptions,
  updateOrganizationSubscription,
} from "../../services/subscriptions/organiztionsSubscriptions";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import { formatDate } from "../../utils/toDate";

import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import SubscriptionStatusBadge from "../../components/SubscriptionStatusBadge";
import SubscriptionSelector from "../../components/SubscriptionSelector";
import Tooltip from "../../components/Tooltip";

const ManageSubscriptions = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");

  const tableTitles = [
    {
      value: "organizationName",
      keepOnMobile: true,
    },
    {
      value: "subscriptionType",
      keepOnMobile: true,
    },
    {
      value: "subscriptionPrice",
      keepOnMobile: true,
    },
    {
      value: "paidAmount",
      keepOnMobile: true,
    },
    {
      value: "subscriptionDate",
      keepOnMobile: true,
    },
    {
      value: "paymentDate",
      keepOnMobile: true,
    },
    {
      value: "subscriptionDuration",
      keepOnMobile: true,
    },
    {
      value: "expirationDate",
      keepOnMobile: true,
    },
    {
      value: "subscriptionStatus",
      keepOnMobile: true,
    },
    {
      value: "Actions",
      keepOnMobile: true,
    },
  ];

  const fetchSubscriptions = async () => {
    setIsLoading(true);
    const data = await getOrganizationSubscriptions({
      page: pagination.currentPage,
      search: searchQuery,
    });
    setSubscriptions(data?.data?.organizations || []);
    setPagination((prev) => ({
      currentPage: data?.data?.currentPage,
      totalPages: data?.data?.totalPages,
      totalRecords: data?.data?.totalRecords,
    }));
    setIsLoading(false);
  };

  const tableRows = subscriptions?.map((sub) => [
    {
      type: TABLE_CELL_TYPES.ELEMENT,
      value: (
        <Tooltip
          maxWidth="600px"
          content={
            <div className="">
              <p className="">
                {sub?.organizationName}
              </p>
              <hr />
              <p className="">
                {sub?.email}
              </p>
            </div>
          }
        >
          {sub?.organizationName.split(" ").slice(0, 3).join(" ")}...
        </Tooltip>
      ),
      keepOnMobile: true,
    },
    {
      value: sub.organizationSubscriptions?.[0]?.subscription.name ?? "",
      type: TABLE_CELL_TYPES.TEXT,
      eclipseText: true,
      keepOnMobile: true,
    },
    {
      value: `${sub.organizationSubscriptions?.[0]?.subscription.pricePerMonth ?? ""} US$`,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: `${sub.organizationSubscriptions?.[0]?.totalPrice ?? ""} US$`,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: sub?.organizationSubscriptions?.[0]?.startDate,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: sub?.organizationSubscriptions?.[0]?.paidAt,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value:
        (sub?.organizationSubscriptions?.[0]?.monthsDuration ?? 0) +
        " " +
        t("months"),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: sub?.organizationSubscriptions?.[0]?.endDate,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: sub?.organizationSubscriptions?.[0]?.status && (
        <SubscriptionStatusBadge
          status={sub?.organizationSubscriptions?.[0]?.status}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          variant={"blank"}
          icon="edit"
          iconSize={18}
          onClick={() => {
            setSelectedSubscription({
              organizationId: sub?.id,
              id: sub?.organizationSubscriptions?.[0]?.id,
              organizationName: sub?.organizationName,
              subscriptionId:
                sub?.organizationSubscriptions?.[0]?.subscription.id,
              subscriptionName:
                sub?.organizationSubscriptions?.[0]?.subscription.name,
              price: sub?.organizationSubscriptions?.[0]?.totalPrice,
              startDate: sub?.organizationSubscriptions?.[0]?.startDate,
              endDate: sub?.organizationSubscriptions?.[0]?.endDate,
              monthsDuration:
                sub?.organizationSubscriptions?.[0]?.monthsDuration,
              status: sub?.organizationSubscriptions?.[0]?.status,
              paidAt: sub?.organizationSubscriptions?.[0]?.paidAt,
            });
            setShowEditModal(true);
          }}
          title={t("edit")}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchSubscriptions();
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [pagination.currentPage]);

  const onClose = () => {
    setShowEditModal(false);
  };

  return (
    <PageWrapper
      title={t("subscriptions")}
      style={{ direction: currentDirection }}

    >
      <div className="o-row o-row--gap-size_500 u-margin--top_500">
        <CustomInput
          placeholder={t("search")}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          resetSearch={() => {
            setSearchQuery("");
            fetchSubscriptions();
          }}
          thick
        />

        <CustomButton
          title={t("search")}
          onClick={handleSearch}
          icon="search"
          iconSize={15}
        />
      </div>
      <div className="o-column o-column--gap-size_500 u-margin--top_500">
        <section>
          {isLoading ? (
            <Loader size={LOADER_SIZES.MEDIUM} />
          ) : (
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
            />
          )}
        </section>
      </div>
      {showEditModal && (
        <EditSubscriptionModal
          open={showEditModal}
          onClose={onClose}
          subscription={selectedSubscription}
          onSuccess={() => {
            fetchSubscriptions();
          }}
        />
      )}
    </PageWrapper>
  );
};

const EditSubscriptionModal = ({ open, onClose, subscription, onSuccess }) => {
  const { t } = useTranslation("general");
  const [subscriptionData, setSubscriptionData] = useState({
    subscriptionId: subscription?.subscriptionId || "",
    startDate: subscription?.startDate || new Date(),
    endDate: subscription?.endDate || new Date(),
    monthsDuration: subscription?.monthsDuration || 0,
    status: subscription?.status || "",
    paidAt: subscription?.paidAt || new Date(),
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation functions
  const validateSubscriptionType = (value) => {
    if (!value || value.trim() === "") {
      return t("subscriptionRequired");
    }
    if (value.length < 2) {
      return t("subscriptionTypeMinLength");
    }
    return "";
  };

  const validateMonthsDuration = (value) => {
    const numValue = parseInt(value);
    if (!value || isNaN(numValue)) {
      return t("monthsDurationRequired");
    }
    if (numValue < 1) {
      return t("monthsDurationMinValue");
    }
    return "";
  };

  const validateDate = (value, fieldName) => {
    if (!value) {
      return t(`${fieldName}Required`);
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return t(`${fieldName}Invalid`);
    }
    return "";
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setSubscriptionData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateStartDateMustBeInFuture = (value) => {
    const startDateOnly = new Date(value).setHours(0, 0, 0, 0);
    const todayOnly = new Date(new Date()).setHours(0, 0, 0, 0);

    if (startDateOnly < todayOnly) {
      return t("startDateMustBeInTheFuture");
    }
    return "";
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Validate subscription type
    const subscriptionTypeError = validateSubscriptionType(
      subscriptionData.subscriptionId,
    );

    if (subscriptionTypeError)
      newErrors.subscriptionType = subscriptionTypeError;

    // Validate months duration
    const monthsDurationError = validateMonthsDuration(
      subscriptionData.monthsDuration,
    );
    if (monthsDurationError) newErrors.monthsDuration = monthsDurationError;

    // Validate payment date
    const paidAtError = validateDate(subscriptionData.paidAt, "paymentDate");
    if (paidAtError) newErrors.paidAt = paidAtError;

    // Validate start date
    const startDateError = validateDate(
      subscriptionData.startDate,
      "startDate",
    );
    if (startDateError) newErrors.startDate = startDateError;

    const startDateMustBeInFuture = validateStartDateMustBeInFuture(
      subscriptionData.startDate,
    );
    if (startDateMustBeInFuture) newErrors.startDate = startDateMustBeInFuture;

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      setErrors({});
      if (!subscription.id) {
        await createOrganizationSubscription(subscription.organizationId, {
          subscriptionId: subscriptionData.subscriptionId,
          monthsDuration: Number(subscriptionData.monthsDuration),
          startDate: new Date(subscriptionData.startDate),
          paidAt: new Date(subscriptionData.paidAt),
        });
      } else {
        await updateOrganizationSubscription(subscription.id, {
          subscriptionId: subscriptionData.subscriptionId,
          monthsDuration: Number(subscriptionData.monthsDuration),
          startDate: new Date(subscriptionData.startDate),
          paidAt: new Date(subscriptionData.paidAt),
        });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error updating subscription:", error);
      setErrors({ submit: t("updateError") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formError =
    Object.keys(errors).length > 0
      ? errors.submit
        ? errors.submit
        : t("inputsCheckError")
      : null;

  return (
    <Modal
      title="editSubscription"
      onClose={onClose}
      open={open}
      error={formError}
      footer={
        <>
          {isSubmitting ? <Loader size={LOADER_SIZES.SMALL} /> : null}
          <section className="edit-user-actions">
            <CustomButton
              title={isSubmitting ? t("saving") : t("confirm")}
              icon="save"
              iconSize={16}
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
            <CustomButton
              type="button"
              title={t("cancel")}
              variant="blank"
              icon="close"
              iconSize={14}
              onClick={onClose}
              disabled={isSubmitting}
            />
          </section>
        </>
      }
    >
      <section>
        {formError && <ErrorBox>{formError}</ErrorBox>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="o-column o-column--gap-size_500">
            <div className="o-column o-column--gap-size_200">
              <span>{t("subscriptionType")}: </span>
              {errors.subscriptionType && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.subscriptionType}
                </span>
              )}
              <SubscriptionSelector
                value={subscriptionData.subscriptionId}
                onChange={(value) => {
                  handleInputChange("subscriptionId", value);
                }}
              />
            </div>

            <div className="o-column o-column--gap-size_200">
              <span>{t("subscriptionDuration")}: </span>
              {errors.monthsDuration && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.monthsDuration}
                </span>
              )}
              <div className="o-row o-row--gap-size_200 o-row--vertical-align_center">
                <div className="o-column o-column--gap-size_100 u-width_full-width">
                  <CustomInput
                    type="number"
                    min="1"
                    value={subscriptionData.monthsDuration}
                    onChange={(e) =>
                      handleInputChange("monthsDuration", e.target.value)
                    }
                    style={{
                      borderColor: errors.monthsDuration ? "red" : undefined,
                    }}
                  />
                </div>
                <span> {t("months")}</span>
              </div>
            </div>

            <div className="o-column o-column--gap-size_200">
              <span>{t("paymentDate")}: </span>
              {errors.paidAt && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.paidAt}
                </span>
              )}
              <CustomInput
                type="date"
                value={formatDate(subscriptionData.paidAt || new Date())}
                onChange={(e) => handleInputChange("paidAt", e.target.value)}
                style={{ borderColor: errors.paidAt ? "red" : undefined }}
              />
            </div>

            <div className="o-column o-column--gap-size_200">
              <span>{t("activationDate")}: </span>
              {errors.startDate && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.startDate}
                </span>
              )}
              <CustomInput
                type="date"
                value={formatDate(subscriptionData.startDate || new Date())}
                onChange={(e) => handleInputChange("startDate", e.target.value)}
                style={{ borderColor: errors.startDate ? "red" : undefined }}
              />
            </div>
          </div>
        </form>
      </section>
    </Modal>
  );
};

export default ManageSubscriptions;
